import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { ChevronDown, ChevronRight, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import CustomTooltip from "./custom-tooltip";
import GraphDbDialog from "./graph-db-dialog";
import { CUSTOM_SCROLLBAR } from "@/lib/constants";
import {
  GRAPH_DB_KINDS,
  GRAPH_DB_KIND_TITLE,
  describeProperties,
  type GraphDbKind,
} from "@/lib/graph-db-schema";
import {
  useDeleteGraphDbItem,
  useGetGraphDbItems,
  type GraphDbItem,
} from "@/service-hooks/use-get-graph-db-ontology-service";

interface GraphDbOntologySectionProps {
  projectUuid: string;
  dataLakeId: string;
  dataLakeLabel?: string;
  isViewer?: boolean;
}

/** The second line of a row: what is worth reading at a glance for this kind. */
const summary = (kind: GraphDbKind, item: GraphDbItem): string => {
  if (kind === "edge-type") return `${item.from || "?"} → ${item.to || "?"}`;
  if (kind === "index") return [item.kind, item.definition].filter(Boolean).join(" · ");
  return describeProperties(item.properties);
};

interface KindCardProps {
  kind: GraphDbKind;
  projectUuid: string;
  dataLakeId: string;
  isViewer?: boolean;
  nodeTypeNames: string[];
  onNodeTypes?: (names: string[]) => void;
}

function KindCard({ kind, projectUuid, dataLakeId, isViewer, nodeTypeNames, onNodeTypes }: KindCardProps) {
  const query = useGetGraphDbItems(kind, dataLakeId, projectUuid);
  const remove = useDeleteGraphDbItem();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<GraphDbItem | undefined>();

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? items.length;

  // Tell the parent which node types exist, so the edge-type dialog can offer them.
  const namesKey = items.map((i) => i.name).join("\u0000");
  useEffect(() => {
    if (kind === "node-type") onNodeTypes?.(namesKey ? namesKey.split("\u0000") : []);
  }, [kind, namesKey, onNodeTypes]);

  const onAdd = () => {
    if (isViewer) {
      toast.error("You have view-only access to this project");
      return;
    }
    setEditing(undefined);
    setDialogOpen(true);
  };

  const onDelete = async (item: GraphDbItem) => {
    if (!item.id) return;
    try {
      await remove.mutateAsync({ kind, id: item.id, projectUuid, dataLakeId });
    } catch {
      // toast handled in mutation
    }
  };

  return (
    <div className="bg-surface-card rounded-lg p-4 border border-surface-border">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <h4 className="text-xs font-semibold text-white">{GRAPH_DB_KIND_TITLE[kind]}</h4>
          <span className="text-xs text-muted bg-surface-dark px-2 py-1 rounded">{total}</span>
          {query.isFetching && <Loader2 className="w-4 h-4 animate-spin text-muted shrink-0" />}
        </div>
        {!isViewer && (
          <CustomTooltip text={`Add ${GRAPH_DB_KIND_TITLE[kind].toLowerCase().replace(/s$/, "")}`}>
            <Button variant="outline" size="icon" className="bg-primary hover:bg-primary-dark" onClick={onAdd}>
              <Plus className="h-4 w-4" />
            </Button>
          </CustomTooltip>
        )}
      </div>

      {query.isError ? (
        <div className="text-xs text-error text-center py-3">{(query.error as Error)?.message || "Failed to load"}</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-muted text-center py-3">
          {query.isFetching ? "Loading..." : `No ${GRAPH_DB_KIND_TITLE[kind].toLowerCase()} yet.`}
        </div>
      ) : (
        <div className={`grid grid-cols-1 gap-2 max-h-72 overflow-y-auto pr-1 ${CUSTOM_SCROLLBAR}`}>
          {items.map((item) => (
            <div
              key={item.id ?? item.name}
              className="group flex items-center justify-between px-3 py-2.5 rounded border border-surface-border bg-surface-active hover:bg-surface-hover"
            >
              <div className="flex-1 min-w-0 text-white">
                <div className="text-xs font-medium truncate" title={item.name}>{item.name}</div>
                {summary(kind, item) && (
                  <div className="text-xs text-muted truncate mt-0.5" title={summary(kind, item)}>
                    {summary(kind, item)}
                  </div>
                )}
                {item.description && kind !== "index" && (
                  <div className="text-xs text-muted truncate mt-0.5" title={item.description}>{item.description}</div>
                )}
              </div>
              {!isViewer && (
                <div className="flex gap-2 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <CustomTooltip text="Edit">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        setEditing(item);
                        setDialogOpen(true);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </CustomTooltip>
                  <CustomTooltip text="Delete">
                    <Button variant="destructive" size="icon" disabled={!item.id || remove.isPending} onClick={() => onDelete(item)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </CustomTooltip>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <GraphDbDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        kind={kind}
        projectUuid={projectUuid}
        dataLakeId={dataLakeId}
        nodeTypeNames={nodeTypeNames}
        existing={editing}
      />
    </div>
  );
}

/**
 * Graph-database schema for a DataLake: node types, edge types and indexes, three flat lists. Collapsed until
 * opened, so a DataLake that holds no graph schema costs nothing. This records a design; it never connects to a
 * database.
 */
export default function GraphDbOntologySection({
  projectUuid,
  dataLakeId,
  dataLakeLabel,
  isViewer,
}: GraphDbOntologySectionProps) {
  const [open, setOpen] = useState(false);
  const [nodeTypeNames, setNodeTypeNames] = useState<string[]>([]);

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 text-left bg-surface-card rounded-lg px-4 py-3 border border-surface-border"
        aria-expanded={open}
      >
        {open ? <ChevronDown className="h-4 w-4 text-muted" /> : <ChevronRight className="h-4 w-4 text-muted" />}
        <span className="text-xs font-semibold text-white">Graph database</span>
        <span className="text-xs text-muted truncate">
          {dataLakeLabel ? `— ${dataLakeLabel} · ` : "— "}node types, edge types, indexes
        </span>
      </button>
      {open &&
        GRAPH_DB_KINDS.map((kind) => (
          <KindCard
            key={`${dataLakeId}:${kind}`}
            kind={kind}
            projectUuid={projectUuid}
            dataLakeId={dataLakeId}
            isViewer={isViewer}
            nodeTypeNames={nodeTypeNames}
            onNodeTypes={setNodeTypeNames}
          />
        ))}
    </div>
  );
}
